import { useEffect, useState } from 'react';
import { getAuthToken } from '../utils/authToken';

/**
 * Fetches a document's binary content (or a rendered thumbnail) and returns an
 * `objectURL` suitable for `<img src>` or `<a href>`.
 *
 * Avoids GCS signed URLs entirely — those require a service-account private key
 * which is not present when the backend runs with user ADC. The blob fetch
 * works with any auth setup.
 *
 * @param documentId - UUID of the document
 * @param enabled - when false, no fetch happens (url stays null)
 * @param options.path - 'download' (raw bytes, default) or 'thumbnail' (PNG
 *        render for PDFs, original bytes for images, 404 for others)
 *
 * Returns { url, loading, error, contentType }. `contentType` is the fetched
 * blob's MIME type ('' until loaded) — lets a caller decide how to RENDER the
 * bytes (an <img> for an image, a page render for a PDF — #440) without a
 * second metadata request. Revokes the object URL on unmount.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export const useDocumentBlobUrl = (documentId, enabled = true, { path = 'download' } = {}) => {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [contentType, setContentType] = useState('');

    useEffect(() => {
        if (!enabled || !documentId) return undefined;

        let cancelled = false;
        let objectUrl = null;

        setLoading(true);
        setError(null);
        setUrl(null);
        setContentType('');

        (async () => {
            try {
                const token = getAuthToken();
                const res = await fetch(
                    `${API_BASE_URL}/api/documents/${documentId}/${path}`,
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                if (cancelled) return;
                let type = (blob.type || res.headers.get('content-type') || '').toLowerCase();
                // A file stored without a sniffed type comes back as
                // application/octet-stream; read the magic bytes so a PDF is
                // still recognised as one.
                if (!type || type.startsWith('application/octet-stream')) {
                    try {
                        const head = await blob.slice(0, 5).text();
                        if (head === '%PDF-') type = 'application/pdf';
                    } catch (_) { /* keep the declared type */ }
                }
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setContentType(type);
                setUrl(objectUrl);
            } catch (err) {
                if (!cancelled) setError(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [documentId, enabled, path]);

    return { url, loading, error, contentType };
};

export default useDocumentBlobUrl;
